import { useMutation, useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'

import { YEARBOOK_PAGES_TABS } from 'data/constants'
import useAuth from 'data/gradoo/hooks/useAuth'
import { CHECK_FOR_CUSTOM_COVER } from 'data/layoutcreator/queries/cover'
import { GET_PROJECT_COVER_DOCUMENTS } from 'data/layoutcreator/queries/documents'
import { GET_PROJECT_META } from 'data/layoutcreator/queries/projects'
import Head from 'shared/components/Head'
import PagePreviewModal from 'shared/components/PageList/components/modals/PagePreviewModal'
import Tools from 'shared/components/PageListTools'
import { useLiveUsers } from 'shared/hooks/useLiveUsers'
import DefaultLayout from 'shared/layout/Default'
import { CoverSides, CoverTypes } from 'shared/types/global'
import Flex from 'shared/ui/Flex'

import { COPY_COVER_TEMPLATES } from '../../data/layoutcreator/mutations/projects'
import LiveCursorWrapper from '../../shared/components/PageList/components/LiveCursorWrapper'
import CoverPage from './components/CoverPage'
import { CoverPageSkeleton } from './components/CoverPage/Skeleton'
import EmptyInside from './components/EmptyInside'

export default function YearbookCover() {
  const navigate = useNavigate()
  const liveUsers = useLiveUsers()
  const { authGroupId } = useAuth()
  const { projectId } = useParams<{
    projectId: string
  }>()
  const { formatMessage } = useIntl()
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)
  const [previewDocument, setPreviewDocument] = useState<{
    document: any
    customCoverPreviewPath?: string
    side: CoverSides
  } | null>(null)

  const { data: projectMetaData, loading: loadingProjectMeta } =
    useQuery(GET_PROJECT_META, {
      variables: {
        projectId: projectId || ''
      },
      skip: !authGroupId || !projectId
    })

  const { data, loading: loadingCoverDocuments } = useQuery(
    GET_PROJECT_COVER_DOCUMENTS,
    {
      variables: {
        groupId: authGroupId || '',
        projectId: projectId || ''
      },
      skip: !authGroupId || !projectId || !projectMetaData
    }
  )

  const {
    data: checkForCustomCoverData,
    loading: loadingCheckForCustomCover
  } = useQuery(CHECK_FOR_CUSTOM_COVER, {
    variables: {
      projectId: projectId || ''
    },
    skip: !projectId
  })

  const isHardCoverType =
    !loadingProjectMeta &&
    !loadingCoverDocuments &&
    projectMetaData?.getProjectMeta.coverType === CoverTypes.hard

  const [copyCoverTemplates] = useMutation(COPY_COVER_TEMPLATES)

  const handleOpenPreview = (
    document: any,
    side: CoverSides,
    customCoverPreviewPath?: string
  ) => {
    setPreviewDocument({ document, customCoverPreviewPath, side })
    setIsPreviewModalOpen(true)
  }

  const handleClosePreview = () => {
    setIsPreviewModalOpen(false)
    setPreviewDocument(null)
  }

  useEffect(() => {
    if (
      authGroupId &&
      projectId &&
      Array.isArray(data?.findProjectCoverDocuments) &&
      data?.findProjectCoverDocuments.length === 0
    ) {
      toast.promise(
        copyCoverTemplates({
          variables: {
            groupId: authGroupId,
            projectName: projectMetaData?.getProjectMeta?.name || ''
          },
          refetchQueries: [
            {
              query: GET_PROJECT_COVER_DOCUMENTS,
              variables: {
                groupId: authGroupId,
                projectId: projectId
              }
            }
          ]
        }),
        {
          loading: formatMessage({
            id: 'Cover.copying.loading'
          }),
          success: formatMessage({
            id: 'Cover.copying.success'
          }),
          error: formatMessage({
            id: 'Cover.copying.error'
          })
        }
      )
    }
  }, [
    data?.findProjectCoverDocuments,
    projectMetaData?.getProjectMeta?.name,
    authGroupId,
    projectId
  ])

  return (
    <>
      <DefaultLayout
        liveUsers={liveUsers}
        RightComponent={
          <Tools isDisabled settingsPath={`/${projectId}/settings`} />
        }
        onBack={() => navigate('/')}
        tabs={YEARBOOK_PAGES_TABS}
      >
        <LiveCursorWrapper>
          <Head titleId="Page.editor.title" />
          <Flex
            columnGap={32}
            marginLeft={48}
            marginRight={48}
            marginBottom={48}
            marginTop={48}
            alignItems="stretch"
            height="calc(100vh - 160px)"
          >
            {loadingProjectMeta ||
            loadingCoverDocuments ||
            loadingCheckForCustomCover ||
            (data?.findProjectCoverDocuments &&
              data.findProjectCoverDocuments.length === 0) ? (
              <>
                <CoverPageSkeleton />
                <CoverPageSkeleton />
              </>
            ) : (
              <>
                {checkForCustomCoverData?.checkForCustomCover &&
                  data?.findProjectCoverDocuments
                    .filter(doc => {
                      return !(
                        projectMetaData?.getProjectMeta.coverType ===
                          CoverTypes.hard &&
                        doc.name.includes('inside')
                      )
                    })
                    .map(document => (
                      <CoverPage
                        document={document}
                        onOpenPreview={(doc, side, customPath) =>
                          handleOpenPreview(doc, side, customPath)
                        }
                        customCoverPreviewPath={
                          checkForCustomCoverData
                            ?.checkForCustomCover?.[
                            document.name.includes('inside')
                              ? 'inside'
                              : 'outside'
                          ]
                        }
                        key={document.id}
                        side={
                          document.name.includes('inside')
                            ? CoverSides.inside
                            : CoverSides.outside
                        }
                      />
                    ))}
                {isHardCoverType &&
                data?.findProjectCoverDocuments &&
                data?.findProjectCoverDocuments?.length > 0 ? (
                  <EmptyInside />
                ) : null}
              </>
            )}
          </Flex>
        </LiveCursorWrapper>
      </DefaultLayout>
      {projectId && authGroupId && previewDocument ? (
        <PagePreviewModal
          isCoverPreview
          projectId={projectId}
          groupId={authGroupId}
          previewUrl={
            previewDocument.customCoverPreviewPath
              ? `${process.env.REACT_APP_REST_URL}${previewDocument.customCoverPreviewPath}`
              : previewDocument.document.previewUrl
          }
          pageNumber={1}
          pageType={
            previewDocument.side === CoverSides.inside
              ? 'cover.inside'
              : 'cover.outside'
          }
          pageId={previewDocument.document.id}
          isOpen={isPreviewModalOpen}
          closeModal={handleClosePreview}
        />
      ) : null}
    </>
  )
}
